import { createDeserializer, handleJsonApiError } from "@jsonapi-serde/client";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
    notificationCaseSchema,
    notificationNoteSchema,
    notificationSchema,
} from "@/components/Forms/schema/notificationSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";

const resourceSelector = createDeserializer({
    type: "notification",
    cardinality: "many",
    attributesSchema: notificationSchema,
    relationships: {
        case: {
            type: "case",
            cardinality: "one",
            included: {
                attributesSchema: notificationCaseSchema,
            },
        },
        note: {
            type: "note",
            cardinality: "one",
            included: {
                attributesSchema: notificationNoteSchema,
            },
        },
    },
});

export const notificationsSelector = (input: unknown) => resourceSelector(input).data;
export type Notifications = ReturnType<typeof notificationsSelector>;
export type Notification = Notifications[number];

export const useFetchNotificationsForUser = (): UseQueryResult<Notifications> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case-notifications"],
        queryFn: async () => {
            const response = await fetch(apiUrl("/notification/for-current-user"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            await handleJsonApiError(response);

            return await response.json();
        },
        select: notificationsSelector,
        staleTime: 30_000, // 30 seconds - notifications change more frequently
        refetchInterval: 60_000, // Poll every 60 seconds for new notifications
        refetchOnWindowFocus: true, // Refetch when user returns to window
        refetchOnMount: false, // Use cached data on mount
    });
};
